import { useCallback } from 'react'
import { usePlayground } from '../store'
import { AppHeader } from './AppHeader'
import { MobileNav } from './MobileNav'
import { StructurePanel } from './StructurePanel'
import { GraphPanel } from './GraphPanel'
import { Sidebar } from './Sidebar'
import { ResizeHandle } from './ResizeHandle'
import { PrivacyNotice } from './PrivacyNotice'

const LEFT_MIN = 280
const LEFT_MAX = 500
const RIGHT_MIN = 300
const RIGHT_MAX = 550

export function AppShell() {
    const isMobile = usePlayground((s) => s.isMobile)
    const activeTab = usePlayground((s) => s.activeTab)
    const leftOpen = usePlayground((s) => s.leftOpen)
    const rightOpen = usePlayground((s) => s.rightOpen)
    const leftWidth = usePlayground((s) => s.leftWidth)
    const rightWidth = usePlayground((s) => s.rightWidth)
    const setLeftOpen = usePlayground((s) => s.setLeftOpen)
    const setRightOpen = usePlayground((s) => s.setRightOpen)
    const setLeftWidth = usePlayground((s) => s.setLeftWidth)
    const setRightWidth = usePlayground((s) => s.setRightWidth)

    const handleLeftResize = useCallback((delta: number) => {
        setLeftWidth(Math.max(LEFT_MIN, Math.min(LEFT_MAX, leftWidth + delta)))
    }, [leftWidth, setLeftWidth])

    const handleRightResize = useCallback((delta: number) => {
        setRightWidth(Math.max(RIGHT_MIN, Math.min(RIGHT_MAX, rightWidth + delta)))
    }, [rightWidth, setRightWidth])

    if (isMobile) {
        return (
            <div className="flex flex-col w-screen overflow-hidden bg-bg text-text h-[100dvh]">
                <AppHeader />
                <div className="flex-1 relative overflow-hidden" style={{ marginBottom: 'calc(48px + env(safe-area-inset-bottom, 0px))' }}>
                    <div className="absolute inset-0" style={{ visibility: activeTab === 'source' ? 'visible' : 'hidden', zIndex: activeTab === 'source' ? 1 : 0 }}><StructurePanel /></div>
                    <div className="absolute inset-0" style={{ visibility: activeTab === 'graph' ? 'visible' : 'hidden', zIndex: activeTab === 'graph' ? 1 : 0 }}><GraphPanel /></div>
                    <div className="absolute inset-0" style={{ visibility: activeTab === 'narrative' ? 'visible' : 'hidden', zIndex: activeTab === 'narrative' ? 1 : 0 }}><Sidebar /></div>
                </div>
                <MobileNav />
                <PrivacyNotice />
            </div>
        )
    }

    return (
        <div className="flex flex-col w-screen overflow-hidden bg-bg text-text h-[100dvh]">
            <AppHeader />

            <div className="flex flex-1 overflow-hidden flex-nowrap">
                {/* Left panel */}
                {leftOpen && <>
                    <aside className="shrink-0 overflow-hidden" style={{ width: `${leftWidth}px`, background: 'var(--bg-subtle)' }}>
                        <div style={{ width: `${leftWidth}px` }} className="h-full overflow-hidden">
                            <StructurePanel />
                        </div>
                    </aside>
                    <ResizeHandle side="left" onResize={handleLeftResize} onCollapse={() => setLeftOpen(false)} minWidth={LEFT_MIN} />
                </>}

                {/* Center: always full canvas */}
                <main className="flex-1 relative overflow-hidden min-w-0">
                    <GraphPanel />
                </main>

                {/* Right panel */}
                {rightOpen && <>
                    <ResizeHandle side="right" onResize={handleRightResize} onCollapse={() => setRightOpen(false)} minWidth={RIGHT_MIN} />
                    <aside className="shrink-0 overflow-hidden" style={{ width: `${rightWidth}px`, background: 'var(--bg-subtle)' }}>
                        <div style={{ width: `${rightWidth}px` }} className="h-full overflow-hidden">
                            <Sidebar />
                        </div>
                    </aside>
                </>}
            </div>
            <PrivacyNotice />
        </div>
    )
}
